'use client';

import { Fragment, SyntheticEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { FormContactInfo } from '@/components/Form/FormContactInfo/FormContactInfo';
import { MobileTabletFilters } from '@/components/Inventory/InventoryFilters/MobileTabletFilters';
import { inventoryGridClassName } from '@/components/Inventory/InventoryGrid';
import { CustomInventoryItem, InventoryItem } from '@/components/Inventory/InventoryItem';
import { inTransitDefaultImageUrl, ITEMS_PER_PAGE_MOBILE, ITEMS_PER_PAGE_TABLET } from '@/constants/inventory';
import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { getItems } from '@/lib/actions';
import { convertFilterSection, convertSortBy } from '@/tools/inventory';
import { isMobile, isTablet } from '@/tools/views';
import { IDynamicsOptionSet } from '@/types/dynamicsForms';
import { FormType } from '@/types/forms';
import { SortBy } from '@/types/inventory';
import { Grid, LaMesaRecVanLogo, Pagination, Subtitle } from '@lamesarv-sdk/components';
import { GridInventory } from '@lamesarv-sdk/inventory';
import { IBasicInventory, IMetaData, InventoryField, PropsWithClasses } from '@lamesarv-sdk/types';

const RefineSortBy = (props: PropsWithClasses) => {
  const { sortBy, setSortBy } = useContext(NewInventoryFiltersContext);

  const items = [
    { label: SortBy.RECOMMENDED, value: SortBy.RECOMMENDED, classes: 'w-36' },
    { label: SortBy.NEWEST, value: SortBy.NEWEST, classes: 'w-24' },
    { label: SortBy.OLDEST, value: SortBy.OLDEST, classes: 'w-24' },
    { label: SortBy.PRICE_LOW_TO_HIGH, value: SortBy.PRICE_LOW_TO_HIGH, classes: 'w-42' },
    { label: SortBy.PRICE_HIGH_TO_LOW, value: SortBy.PRICE_HIGH_TO_LOW, classes: 'w-42' },
  ];

  const handleChange = (e: SyntheticEvent) => {
    const value = (e.target as HTMLInputElement).value;

    setSortBy(value as SortBy);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortByClasses = items.find((item) => item.value === sortBy)?.classes || 'w-24';

  return (
    <div className="flex flex-row items-center gap-2">
      <label htmlFor="select-sort-by" className="text-md-regular text-black-500">
        Sort By:
      </label>
      <select
        id="select-sort-by"
        className={twMerge(
          'p-0 pr-8 border-none text-md-regular text-black-350 focus:outline-none focus:border-none focus:ring-0',
          sortByClasses,
          props.className,
        )}
        value={sortBy}
        onChange={handleChange}
        data-testid="select-sort-by"
      >
        {items.map((option, index) => {
          return (
            <option key={option.value + index} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
    </div>
  );
};

const RefinePerPage = (props: PropsWithClasses) => {
  const { perPage, setPerPage } = useContext(NewInventoryFiltersContext);

  const items = [
    { label: '15', value: 15 },
    { label: '30', value: 30 },
    { label: '45', value: 45 },
  ];

  const handleChange = (e: SyntheticEvent) => {
    const value = parseInt((e.target as HTMLInputElement).value, 10);
    console.log('Selected perPage:', value);
    setPerPage(value);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-row items-center gap-2">
      <label htmlFor="select-per-page" className="text-md-regular text-black-500">
        Units per Page:
      </label>
      <select
        id="select-per-page"
        className={twMerge(
          'p-0 pr-8 border-none text-md-regular text-black-350 leading-none focus:outline-none focus:border-none focus:ring-0',
          props.className,
        )}
        value={perPage}
        onChange={handleChange}
        data-testid="select-per-page"
      >
        {items.map((item, index) => {
          return (
            <option key={item.value + index} value={String(item.value)}>
              {item.label}
            </option>
          );
        })}
      </select>
    </div>
  );
};

interface IRefinePaginationProps extends PropsWithClasses {
  nbHits: number;
  callback?: (page: number) => void;
}

const RefinePagination = (props: IRefinePaginationProps) => {
  const { page, setPage, perPage } = useContext(NewInventoryFiltersContext);

  const callback = (currentPage: number) => {
    setPage(currentPage);

    if (props.callback) {
      props.callback(currentPage);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const customLegend = (amountFrom: number, amountTo: number, totalItems: number) => {
    let legendText = '';

    if (totalItems === 0) {
      legendText = 'No results found';
    }
    if (totalItems === 1) {
      legendText = '1 result found';
    }

    legendText = `Showing ${amountFrom} - ${amountTo} of ${totalItems}`;

    return <Fragment>{legendText}</Fragment>;
  };

  const classesBase = 'text-[16px] px-0 w-[40px] rounded-[4px]';
  const classesItemsNavigation = 'flex flex-row w-auto px-2';
  const classesNormal = 'bg-white border-black-200 text-black-500';
  const classesDisabled = 'bg-white border-black-100 text-black-200 cursor-not-allowed';
  const classesSelected = 'bg-black border-black text-white';

  return (
    <Pagination
      totalItems={props.nbHits}
      pageSize={perPage}
      radix={0}
      offsetStart={3}
      offsetEnd={3}
      callback={callback}
      customLabelNext="Next"
      customLabelPrevious="Back"
      displayAlwaysNavigation={true}
      className={props.className}
      currentPage={page ?? 1}
      classesItemsPrevious={classesItemsNavigation}
      classesItemsNext={classesItemsNavigation}
      classesLegend="text-[16px] text-black-350"
      classesBase={classesBase}
      classesNormal={classesNormal}
      classesDisabled={classesDisabled}
      classesSelected={classesSelected}
      customLegend={customLegend}
    />
  );
};

const newInventoryGridClassName = twMerge(inventoryGridClassName, 'lg:gap-x-0 xl:gap-x-2');

interface NoResultsProps {
  locationsOptionSet?: IDynamicsOptionSet;
  metadata: IMetaData;
}

const NoResults = ({ metadata, locationsOptionSet }: NoResultsProps) => {
  const { query } = useContext(NewInventoryFiltersContext);

  return (
    <div className="">
      <Subtitle content="No Search Results found." className="normal-case font-medium text-lg" />
      <FormContactInfo
        formType={FormType.contactInfo}
        title="Tell us what are you looking for"
        tagItem="search-not-found"
        locationsOptionSet={locationsOptionSet}
      />
      <h2 className="mt-7 mb-2 px-2 w-full text-lg text-sage-700 uppercase font-bold">
        Below are some units you might be interested in.
      </h2>
      <GridInventory
        filters={`${InventoryField.isFlashSale}:=true`}
        queryConditional={query}
        baseCols={1}
        smCols={2}
        mdCols={2}
        lgCols={3}
        xlCols={3}
        gap={6}
        metadata={metadata}
        defaultImageUrl={inTransitDefaultImageUrl}
        CustomInventoryItem={CustomInventoryItem}
        className={newInventoryGridClassName}
      />
    </div>
  );
};

interface ISearchResultsProps {
  locationsOptionSet?: IDynamicsOptionSet;
  metadata: IMetaData;
}

export const SearchResults = ({ metadata, locationsOptionSet }: ISearchResultsProps) => {
  const { filters, baseFilters, sortBy, query, page, perPage: perPageDesktop } = useContext(NewInventoryFiltersContext);

  const [items, setItems] = useState<IBasicInventory[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currentMobilePageRef = useRef<number>(1);

  const _getItems = useCallback(
    (currentPage?: number) => {
      const finalFilters = convertFilterSection(filters, baseFilters);
      const sort = convertSortBy(sortBy);

      let perPage = perPageDesktop;
      if (isMobile()) {
        perPage = ITEMS_PER_PAGE_MOBILE;
      } else if (isTablet()) {
        perPage = ITEMS_PER_PAGE_TABLET;
      }

      return getItems({
        filters: finalFilters,
        sort,
        currentPage,
        q: query,
        perPage,
      });
    },
    [filters, baseFilters, sortBy, query, perPageDesktop],
  );

  const getMoreItems = async () => {
    currentMobilePageRef.current++;

    setIsLoading(true);

    try {
      const itemsResult = await _getItems(currentMobilePageRef.current);

      setItems((prevItems) => [...prevItems, ...itemsResult.items]);
      setTotal(itemsResult.total);
    } catch (error) {
      console.error('Error fetching more items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPageItems = (page: number, controller?: AbortController) => {
    setItems([]);
    setTotal(null);
    setIsLoading(true);

    _getItems(page)
      .then((result) => {
        if (controller?.signal?.aborted) return;

        setItems(result.items);
        setTotal(result.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const controller = new AbortController();

    getPageItems(page, controller);

    return () => {
      controller.abort();
    };
  }, [_getItems, page]);

  return (
    <Fragment>
      <MobileTabletFilters total={total} />
      {items.length > 0 && (
        <div className="flex flex-col items-end gap-10 max-lg:py-10">
          <div className="hidden lg:flex flex-col gap-4 md:flex-row items-start md:items-center justify-between w-full pl-6 md:pl-2.5">
            <div className="flex flex-row items-center gap-2">
              <span className="text-md-medium">{total}</span>
              <span className="text-md-regular text-black-350">results</span>
            </div>
            <div className="flex flex-row items-center justify-between gap-4 w-full md:w-auto">
              <RefinePerPage />
              <RefineSortBy />
            </div>
          </div>
          <Grid
            items={items.map((item) => (
              <InventoryItem key={item[InventoryField.id]} item={item} />
            ))}
            baseCols={1}
            smCols={1}
            mdCols={2}
            lgCols={2}
            xlCols={3}
            gap={6}
            className={newInventoryGridClassName}
          />
          <div className="hidden lg:block">
            <RefinePagination nbHits={total} className="font-helvetica items-center px-4" />
          </div>
        </div>
      )}
      {isLoading && !total && (
        <div className="flex flex-row justify-center mt-16 lg:mt-32 animate-pulse">
          <LaMesaRecVanLogo className="h-20" />
        </div>
      )}
      {total === 0 && !isLoading && <NoResults metadata={metadata} locationsOptionSet={locationsOptionSet} />}
      {!!total && total > items.length && (
        <div className="flex justify-center lg:hidden">
          <button
            type="button"
            onClick={getMoreItems}
            disabled={isLoading}
            className="border border-blue-500 rounded bg-white text-blue-500 text-sm font-medium px-6 py-3 flex items-center justify-center enabled:hover:bg-blue-50 disabled:opacity-50 disabled:cursor-wait"
          >
            Load More
          </button>
        </div>
      )}
    </Fragment>
  );
};
